import { BarCustomLayerProps, ResponsiveBar } from "@nivo/bar";
import {
  CATEGORY_COLORS,
  ChartItemInfo,
  formatAxisValue,
  LegendDot,
} from "components/BooksHomePage/ChartComponents";
import ToolTip from "components/design/toolTip";
import { CashBalanceIcon } from "components/icons/CashBalanceIcon";
import { BAR_GAP, BAR_RADIUS, LINE_SPACING } from "constants/booksHomePage";
import { colors } from "constants/colors";
import { motion } from "framer-motion";
import { ReactNode } from "react";
import { AnalysisType, CategoryBreakdown } from "store/apis/financialMetrics";
import { formatAmount } from "utils/foramtAmount";

type TopCategoriesChartProps = {
  categories: CategoryBreakdown[];
  analysisType?: AnalysisType;
  chartId?: string;
  onCategoryClick?: (categoryId: string) => void;
};

type BarDataItem = {
  category: string;
  amount: number;
  color: string;
  uuid: string;
};

const ClickableAmount = ({
  onClick,
  children,
}: {
  onClick?: () => void;
  children: ReactNode;
}) => {
  if (!onClick) {
    return <span className="t-text-white">{children}</span>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="all:unset t-cursor-pointer t-text-white !t-underline t-decoration-neutral-40 hover:t-decoration-white t-transition"
    >
      {children}
    </button>
  );
};

const COLOR_SCHEMES = {
  REVENUE: {
    bar: [
      colors.purple[10],
      colors.purple[10],
      colors.purple[20],
      colors.purple[30],
      colors.purple[40],
    ],
    crossLine: [
      colors.purple[20],
      colors.purple[30],
      colors.purple[40],
      colors.purple[50],
      colors.purple[70],
    ],
  },
  SPEND: {
    bar: [
      colors.neutral[10],
      colors.neutral[10],
      colors.neutral[20],
      colors.neutral[30],
      colors.neutral[40],
    ],
    crossLine: [
      colors.neutral[20],
      colors.neutral[30],
      colors.neutral[40],
      colors.neutral[50],
      colors.neutral[70],
    ],
  },
};

const createHorizontalBar = (
  crossLineOpacity: number = 1,
  chartId: string = "default",
  onCategoryClick?: (categoryId: string) => void,
  analysisType: AnalysisType = "CATEGORY"
) => {
  return (props: BarCustomLayerProps<BarDataItem>) => {
    const scheme =
      COLOR_SCHEMES[chartId as keyof typeof COLOR_SCHEMES] ??
      COLOR_SCHEMES.REVENUE;
    const totalBars = props.bars.length;

    return (
      <>
        {props.bars.map(({ x, y, width, height, key, data }, barIndex) => {
          if (!width || !height) return null;

          const finalHeight = Math.max(0, Math.abs(height) - BAR_GAP);
          if (finalHeight <= 0) return null;

          const barData = data.data;
          const clipId = `horizontal-bar-clip-${chartId}-${barIndex}`;
          const maxLines = 150;
          const lines = Array.from({ length: maxLines }, (_, index) => index);

          const paletteSize = scheme.bar.length;
          const colorsToUse = scheme.bar.slice(
            Math.max(0, paletteSize - totalBars)
          );
          const crossLinesToUse = scheme.crossLine.slice(
            Math.max(0, paletteSize - totalBars)
          );

          const colorIndex = Math.min(barIndex, colorsToUse.length - 1);

          const barColor = colorsToUse[colorIndex];
          const crossLineColor = crossLinesToUse[colorIndex];
          return (
            <motion.g
              key={key}
              initial={{ x, y }}
              animate={{ x, y }}
              transform={`translate(${x}, ${y})`}
            >
              <defs>
                <clipPath id={clipId}>
                  <rect
                    width={width}
                    height={finalHeight}
                    rx={BAR_RADIUS}
                    ry={BAR_RADIUS}
                  />
                </clipPath>
              </defs>
              <ToolTip
                propClass="!t-bg-neutral-100"
                text={
                  <div className="t-text-start t-flex t-flex-col t-gap-2 t-text-body-sm">
                    <ChartItemInfo
                      dot={<LegendDot color={barColor} />}
                      lable={barData.category}
                      value={
                        <ClickableAmount
                          onClick={
                            onCategoryClick && analysisType === "CATEGORY"
                              ? () => onCategoryClick(barData.uuid)
                              : undefined
                          }
                        >
                          {formatAmount(barData.amount)}
                        </ClickableAmount>
                      }
                    />
                  </div>
                }
              >
                <motion.rect
                  width={width}
                  height={finalHeight}
                  fill={barColor}
                  rx={BAR_RADIUS}
                  ry={BAR_RADIUS}
                />
              </ToolTip>
              <g clipPath={`url(#${clipId})`}>
                {lines.map((index) => (
                  <line
                    key={`line-${index}`}
                    x2={width * 1.5}
                    transform={`translate(0 ${
                      index * LINE_SPACING
                    }) rotate(-43)`}
                    stroke={crossLineColor}
                    strokeWidth={1.5}
                    strokeOpacity={crossLineOpacity}
                  />
                ))}
              </g>
            </motion.g>
          );
        })}
      </>
    );
  };
};

const getNiceTickInterval = (range: number): number => {
  if (range <= 0) return 100;
  const magnitude = Math.pow(10, Math.floor(Math.log10(range)));
  const normalized = range / magnitude;
  if (normalized <= 2) return magnitude / 2;
  if (normalized <= 5) return magnitude;
  return magnitude * 2;
};

const EMPTY_STATE_COPY: Record<
  AnalysisType,
  { title: string; subtitle: string }
> = {
  CATEGORY: {
    title: "No transactions categorised",
    subtitle: "Add/categorise transactions to see top categories",
  },
  CLASSIFICATION: {
    title: "No transactions classified",
    subtitle: "Add/classify transactions to see top classifications",
  },
};

export const TopCategoriesChart = ({
  categories: categoriesProp,
  analysisType = "CATEGORY",
  chartId = "default",
  onCategoryClick,
}: TopCategoriesChartProps) => {
  const categories = categoriesProp ?? [];

  if (!categories.length) {
    const copy = EMPTY_STATE_COPY[analysisType];
    return (
      <div className="t-flex t-flex-col t-justify-center t-items-center t-h-64 t-text-text-30 sm:t-text-center sm:t-px-4">
        <CashBalanceIcon />
        <span className="t-pt-4 t-text-body-sm">{copy.title}</span>
        <span className="t-text-body-sm t-text-text-30 t-mt-2">
          {copy.subtitle}
        </span>
      </div>
    );
  }

  // Reverse so largest bar is at top
  const chartData = categories
    .slice(0, 6)
    .map((cat, index) => ({
      category: cat.name,
      amount: cat.amount,
      color: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
      uuid: cat.uuid,
    }))
    .reverse();

  const maxAmount = Math.max(...chartData.map((d) => d.amount), 0);
  const tickInterval = getNiceTickInterval(maxAmount);
  const tickCount = Math.ceil(maxAmount / tickInterval) + 1;
  const tickValues = Array.from(
    { length: tickCount },
    (_, i) => i * tickInterval
  );
  const isSingleBar = chartData.length === 1;

  return (
    <div className="t-h-64">
      <ResponsiveBar
        data={chartData}
        keys={["amount"]}
        indexBy="category"
        layout="horizontal"
        margin={{ top: 10, right: 0, bottom: 40, left: 60 }}
        padding={isSingleBar ? 0.5 : 0.3}
        colors={colors.purple[30]}
        borderRadius={BAR_RADIUS}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 0,
          tickPadding: 10,
          tickValues,
          format: formatAxisValue,
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 10,
          format: (value) => {
            const str = String(value);
            return str.length > 8 ? `${str.slice(0, 6)}...` : str;
          },
        }}
        enableGridX={true}
        enableGridY={false}
        gridXValues={tickValues}
        enableLabel={false}
        layers={[
          "grid",
          "axes",
          createHorizontalBar(0.3, chartId, onCategoryClick, analysisType),
        ]}
        theme={{
          grid: {
            line: {
              stroke: colors.neutral[0],
              strokeDasharray: "4 4",
            },
          },
          axis: {
            ticks: {
              text: {
                fill: colors.text[30],
                fontSize: 12,
              },
            },
          },
        }}
      />
    </div>
  );
};
